import { Injectable } from '@angular/core';
import { ScreenDimensions } from '../shared/models/screen.dimensions.model';
import { Observable, of, forkJoin } from 'rxjs';
import { SerializedParams } from './models/serialized-param.model';
import { BookmarkModel } from '../bookmarks/models/bookmark.model';
import { CameraService } from '../engine/camera.service';
import { NumberService } from '../shared/number.service';
import { CameraModel } from '../engine/models/camera.model';
import { WidgetOptions } from './models/widget-options.model';
import { flatMap, map, catchError } from 'rxjs/operators';
import { ExportService } from '../shared/export.service';
import { saveAs } from 'file-saver';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { UserAppModel } from '../auth/models/user-app.model';
import { PublishExportModel } from './models/publish-export.model';
import { DownloadEntryModel } from './models/download-entry.model';
import { ImageService, ImageTypeEnum } from '../shared/image.service';
import { defaultScreen } from './data/screens';
import { get } from 'lodash';
import { UIConfig } from '../widget-config/initial-config';

const formHeaders = {
  headers: new HttpHeaders({
    'Content-Type':  'application/json; charset=utf-8'
  })
};

const defaultOptions = new WidgetOptions();

export enum PublishLinkType {
  Single = 'share.widget.link',
  Bulk = 'share.widget.export'
}

const embedDefaults = {
  id: 'embedded-human',
  dimensions: defaultScreen.dimensions,
  fullScreen: true
};

@Injectable({
  providedIn: 'root'
})
export class PublishService {

  constructor(
    private http: HttpClient,
    private cameraService: CameraService,
    private numberService: NumberService,
    private exportService: ExportService, 
    private imageService: ImageService) {
  }

  exportImage(imageData: string, contentType: ImageTypeEnum, fileName: string): Observable<boolean> {
    const exportBlob = this.imageService.base64ToBlob(imageData, contentType);
    saveAs(exportBlob, fileName);

    return of(true);
  }

  exportModels(
    models: BookmarkModel[],
    widgetPath: string,
    widgetOptions: WidgetOptions,
    dimensions?: ScreenDimensions,
    lang?: string,
    app?: UserAppModel,
    offlineEnabled: boolean = false,
  ): Observable<boolean> {

    // default export columns
    const userExportColumns = [
      { label: 'Title', field: 'title' },
      { label: 'URL', field: 'url' },
      { label: 'iFrame', field: 'iFrame' },
      { label: 'Author', field: 'author' },
      { label: 'Thumbnail URL', field: 'thumbnail_url' }
    ];
    // optional export columns
    if (offlineEnabled) {
      userExportColumns.push({
        label: 'Offline Package URL',
        field: 'offline_url'
      });
    }

    // get export models
    return this.getExportModels(models, widgetPath, widgetOptions, dimensions, lang, app, offlineEnabled)
      .pipe(
        // export models -> csv
        flatMap((exportedModels) => this.exportService.exportCSV(exportedModels, userExportColumns)),
        // download csv
        map((csv) => {
          // get formatted export name (YYYY-MM-DD)
          const isoDate = new Date().toISOString().split('T')[0];
          const exportFileName = `BioDigital-Widgets-${isoDate}-export.csv`;
          const exportBlob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
          saveAs(exportBlob, exportFileName);

          return true;
        })
      );
  }

  // Generates a list of export models per bookmark
  getExportModels(
    bookmarks: BookmarkModel[],
    widgetPath: string,
    widgetOptions: WidgetOptions,
    dimensions?: ScreenDimensions,
    lang?: string,
    app?: UserAppModel,
    offlineEnabled: boolean = false): Observable<PublishExportModel[]> {

    // generate bulk export UAID (shared) + optional download links
    const uaid$ = app ? of(null) : this.getUAID(PublishLinkType.Bulk);
    const offlineURLs$ = offlineEnabled ? this.getDownloadURLs(bookmarks) : of([]);
    
    return forkJoin(uaid$, offlineURLs$)
      .pipe(
        map(([uaid, offlineURLs]) => {
          return bookmarks.map((bookmark, index) => {
            const { title, author, thumbnail_url } = bookmark;
            const url = this.getWidgetURL(bookmark, widgetPath, widgetOptions, null, uaid, lang, app);
            const embed = this.getWidgetEmbed(url, dimensions);
            const thumbnailURL = this.buildThumbnailURL(thumbnail_url);
            const offlineURL = offlineURLs[index] || '';
            const exportModel: PublishExportModel = {
              title: title,
              url: url,
              iFrame: embed,
              author: author,
              thumbnail_url: thumbnailURL,
              offline_url: offlineURL
            };
            return exportModel;
          });
        })
      );
  }

  /**
   * Gets the widget URL
   */
  generateWidgetURL(
    model: BookmarkModel,
    widgetPath: string,
    widgetOptions: WidgetOptions,
    camera?: CameraModel,
    linkType?: PublishLinkType,
    lang?: string,
    app?: UserAppModel): Observable<string> {

    // generate optional UAID
    const uaid$ = app || !linkType ? of(null) : this.getUAID(linkType);

    return uaid$
      .pipe(
        map((uaid) => this.getWidgetURL(model, widgetPath, widgetOptions, camera, uaid, lang, app))
      );
  }

  getWidgetURL(
    model: BookmarkModel,
    widgetPath: string,
    widgetOptions: WidgetOptions,
    camera?: CameraModel,
    uaid?: string,
    lang: string = 'en',
    app?: UserAppModel): string {

    widgetOptions = widgetOptions || defaultOptions;

    // add content params
    const isBookmark = model.type === 'bookmark';
    const contentParams = {};
    const contentType = isBookmark ? 'be' : 'm';
    const contentId = isBookmark ? model.id : model.module_name;
    contentParams[contentType] = `${contentId}`;

    // expand full list of params
    const serializedParams = {
      ...contentParams,
      ...this.getParamsForUIOptions(widgetOptions || defaultOptions),
      ...this.getParamsForLoadingExperience(widgetOptions || defaultOptions),
      ...this.getParamsForScrollMode(widgetOptions || defaultOptions),
      ...this.getParamsForLoadRotate(widgetOptions || defaultOptions),
      ...this.getParamsForUIOptions(widgetOptions || defaultOptions)
    };

    // smooth BG color
    const colors = get(widgetOptions, 'background.colors');
    if (Array.isArray(colors) && colors.length >= 3) {
      const smoothColor = colors
        .slice(0, 4) // rgb(a)
        .map((value) => this.numberService.roundNumber(value, 3));

      serializedParams['background.colors'] = `${smoothColor}`;
    }

    // smooth camera
    if (camera) {
      const { eye, look, up } = this.cameraService.roundCamera(camera, 3);
      const smoothCamera = [
        eye.x, eye.y, eye.z,
        look.x, look.y, look.z,
        up.x, up.y, up.z
      ];
      serializedParams['camera'] = `${smoothCamera}`;
    }

    // append dev key or uaid
    if (app) { // append developer key
      serializedParams['dk'] = app.app_apikey;
    } else if (uaid) { // append UAID
      serializedParams['uaid'] = uaid;
    }

    // append lang
    if (lang && lang !== 'en') {
      serializedParams['lang'] = lang;
    }

    // special case: always use custom widget
    const targetWidgetPath = this.isCustomWidget(model.embed_url_path) ? model.embed_url_path : widgetPath;
    return this.buildWidgetURL(targetWidgetPath, serializedParams);
  }

  /**
   * Gets the widget embed code
   */
  generateWidgetEmbed(
    model: BookmarkModel,
    widgetPath: string,
    widgetOptions: WidgetOptions,
    dimensions?: ScreenDimensions,
    camera?: CameraModel,
    linkType?: PublishLinkType,
    lang?: string,
    app?: UserAppModel): Observable<string> {
    // get URL
    return this.generateWidgetURL(model, widgetPath, widgetOptions, camera, linkType, lang, app)
      .pipe(
        // compose embed
        map((url) => this.getWidgetEmbed(url, dimensions))
      ); 
  }

  getWidgetEmbed(url: string, dimensions?: ScreenDimensions): string {
    const { width, height } = dimensions || embedDefaults.dimensions;

    return `<iframe
  id="${embedDefaults.id}"
  frameBorder="0"
  width="${width}"
  height="${height}"
  allowFullScreen="${embedDefaults.fullScreen}"
  src="${url}">
</iframe>`;
  }

  getImageEmbed(
    title: string,
    widgetURL: string,
    imageURL: string): string {
    const imageTitle = `BioDigital Human: ${title}`.replace(/"/g, '&quot;'); // encode double quotes
    return `<a href="${widgetURL}" target="_blank"><img src="${imageURL}" alt="${imageTitle}" title="${imageTitle}" /></a>`;
  }

  getUAID(linkType: PublishLinkType): Observable<string> {
    const analytic = {
      analytic_type: `${linkType}`,
      analytic_platform: 'app'
    };
    return this.http.post<string>(`/ws/user/analytics`, analytic, formHeaders)
      .pipe(
        map((response: any) => response.id)
      );
  }

  // generates an ordered list of download links for the specified bookmarks
  getDownloadURLs(models: BookmarkModel[]): Observable<string[]> {
   
    // create a list of download request models 
    const contentModels: DownloadEntryModel[] = models
      .filter((bookmark) => !bookmark.favorite && !bookmark.unavailable) // exclude legacy content
      .map((bookmark) => {
        return {
          type: bookmark.type,
          id: bookmark.type === 'module' ? bookmark.module_name : bookmark.id
        };
      });

    return this.http.post(`/ws/download/reserve`, { content: contentModels }, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((response: any) => {
          const tokens: DownloadEntryModel[] = response.tokens;
          // for each piece of content, generate download URL
          return models.map((bookmark) => {
            const type = bookmark.type;
            const id = bookmark.type === 'module' ? bookmark.module_name : bookmark.id;

            // find token by type, id
            const foundToken = tokens.find((token) => token.type === type && token.id === id);

            // build download from token
            return foundToken && foundToken.token ? this.buildDownloadURL(foundToken.token) : '';
          });
        }),
        catchError(() => of(models.map(() => '')))
      );
  }

  /**
   * Overwrites the target widget options w/ source widget data.
   */
  extendWidgetOptions(targetWidgetOptions: WidgetOptions, sourceWidgetData: any): WidgetOptions {
    // expand flat options
    const sourceWidgetOptions = this.expandWidgetOptions(sourceWidgetData);

    // deep extend from source
    this.deepExtendWidgetOptions(targetWidgetOptions, sourceWidgetOptions);

    return targetWidgetOptions;
  }

  getWidgetConfig(widgetOptions: WidgetOptions): SerializedParams {
    const serializedParams: SerializedParams = {};
    const paramNames = Object.keys(widgetOptions);
    paramNames.forEach((paramName) => {
      const paramValue = widgetOptions[paramName];
      if (paramValue !== null) {
        const normalizedConfigName = this.getConfigName(paramName);
        serializedParams[normalizedConfigName] = paramValue;
      }
    });
    return serializedParams;
  }

  /**
   * i.e., uiInfo -> ui-info -> info
   */
  getConfigName(name: string): string {
    const dashCase = this.camelToDashCase(name);
    const normalizedDashCase = dashCase.replace(/^ui-/g, '');
    const configName = this.dashCaseToCamelCase(normalizedDashCase);
    return configName;
  }


  getParamsForLoadingExperience(params: WidgetOptions) {
    if (params.initial.button) {
      return {'initial.button': true};
    }

    if (params.initial.handHint) {
      return {'initial.hand': true};
    }

    if (params.initial.play) {
      return {'initial.play': true};
    }

    return {};
  }

  getParamsForScrollMode(params: WidgetOptions) {
    if (params.disableScroll.alert) {
      return {'disable-scroll.alert': true};
    }

    if (params.disableScroll.overlay) {
      return {'disable-scroll': true};
    }

    return {'disable-scroll': false};
    
  }

  getParamsForLoadRotate(params: WidgetOptions) {
    if (typeof params.loadRotate === 'number') {
      return {'load-rotate': params.loadRotate};
    }

    return {};
  }


  getParamsForUIOptions(params: WidgetOptions): UIConfig {
    return {
      'ui-info': params.uiInfo,
      'ui-search': params.uiSearch,
      'ui-reset': params.uiReset,
      'ui-fullscreen': params.uiFullscreen,
      'ui-nav': params.uiNav,
      'ui-tools': params.uiTools,
      'ui-help': params.uiHelp,
      'ui-chapter-list': params.uiChapterList,
      'ui-label-list': params.uiLabelList,
      'ui-anatomy-descriptions': params.uiAnatomyDescriptions
    };
  }

  camelToDashCase(input: string): string {
    return input.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  dashCaseToCamelCase(input: string): string {
    return input.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  }

    /**
   * Returns true if the specified widget path is a custom widget.
   */
  isCustomWidget(path: string) {
    return /widgets\/([^\/]+)\//.test(path);
  }

  saveUserImage(imageData: string): Observable<string> {
    return this.http.post<string>(`/ws/user/image/v1-0`, { image_src: imageData }, formHeaders)
      .pipe(
        map((response: any) => response.url)
      );
  }

  private buildWidgetURL(widgetPath: string, widgetParams: SerializedParams): string {
    // Build params
    const encodedParams = Object.entries(widgetParams)
      .map(([key, value]) => {
        return `${key}=${value}`;
      })
      .join('&');

    // strip leading and trailing slashes (/)
    const normalizedWidgetPath = widgetPath.replace(/^\/|\/$/g, '');

    return `${window.location.origin}/${normalizedWidgetPath}/?${encodedParams}`;
  }

  private buildThumbnailURL(thumbPath: string): string {
    return `${window.location.origin}${thumbPath}`;
  }

  private buildDownloadURL(token: string): string {
    return `${window.location.origin}/download?t=${token}`;
  }

  /**
   * Recursively set target widget field values from source data.
   */
  private deepExtendWidgetOptions(target: any, source: any) {

    Object.entries(target).forEach(([targetField, targetValue]) => {
      const targetType = typeof(targetValue);

      // account for variation in source name
      const validFieldNames = [
        // uiCameraMode
        targetField,
        // ui-camera-model
        this.camelToDashCase(targetField),
        // cameraMode
        this.getConfigName(targetField)
      ];

      validFieldNames.forEach((validName) => {        
        if (source.hasOwnProperty(validName) && source[validName] !== null) {
          const sourceValue = source[validName];
          const sourceType = typeof(sourceValue);
          // special case: account for initial value
          if (targetValue === null) {
            target[targetField] = sourceValue;
          } else if (targetType === sourceType) {  // validate types
            // recursive set
            if (targetType === 'object') {
              this.deepExtendWidgetOptions(targetValue, sourceValue);
            } else { // set primitives
              target[targetField] = sourceValue;
            }
          }
        }
      });
    });

  }

  /**
   * expands a flat obect { 'initial.button': true, 'initial.handHint': true } ->
   * {
   *  initial: {
   *    'button': true,
   *    handHint': true
   *  }
   * }
   */
  private expandWidgetOptions(source: any): any {
    const target = {};
    Object.entries(source).forEach(([key, value]) => {
      const components = key.split('.');
      if (components.length > 1) {
        const parentFieldName = components[0];
        const childFieldName = components.slice(1).join('.');
        // create initial entry, else replace non-object types
        if (target[parentFieldName] === null || typeof (target[parentFieldName]) !== 'object') {
          target[parentFieldName] = {};
        }
        target[parentFieldName][childFieldName] = value;
      } else {
        target[key] = source[key];
      }
    });
    return target;
  }
}
